"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { db } from "./client"

type InstantSession = {
  user: {
    id: string
    email?: string
    name?: string
    role?: string
    tier?: string
    avatar?: string
    timezone?: string
    [key: string]: any
  }
} | null

const APP_ID = process.env.NEXT_PUBLIC_INSTANTDB_APP_ID || ""
const IS_MOCK_MODE = !APP_ID || APP_ID === "placeholder_app_id"

const useMockAuth = () => ({ user: null, isLoading: false })
const useAuthHook = IS_MOCK_MODE ? useMockAuth : () => db.useAuth()

// Helper to get stored session from localStorage
const getStoredSession = (): InstantSession | null => {
  if (typeof window === "undefined") return null
  try {
    const storedEmail = localStorage.getItem("user-email") || sessionStorage.getItem("user-email")
    const storedRole = localStorage.getItem("user-role") || sessionStorage.getItem("user-role")
    if (storedEmail && storedRole) {
      return {
        user: {
          id: `stored-${storedEmail}`,
          email: storedEmail,
          role: storedRole,
          tier: localStorage.getItem("user-tier") || sessionStorage.getItem("user-tier") || undefined,
          timezone: localStorage.getItem("user-timezone") || sessionStorage.getItem("user-timezone") || undefined,
        },
      }
    }
  } catch (err) {
    console.warn("⚠️ [SESSION] Failed to read stored session:", err)
  }
  return null
}

export function useSession() {
  const [mockSession, setMockSession] = useState<InstantSession>(null)
  const [mockReady, setMockReady] = useState(false)
  const authErrorRef = useRef(false)
  const [useStoredSession, setUseStoredSession] = useState(false)

  // Always call the hook (React rules) - wrap in error boundary via state
  let user: any = null
  let isLoading = false
  
  // Use error boundary pattern - catch errors in useEffect instead of try-catch around hook
  useEffect(() => {
    // Listen for unhandled IndexedDB errors
    const handleError = (event: ErrorEvent) => {
      const err = event.error
      const isIndexedDBError = err?.message?.includes("IDBDatabase") || 
                              err?.message?.includes("object stores") ||
                              err?.name === "NotFoundError" ||
                              err?.message?.includes("Failed to execute")
      
      if (isIndexedDBError && !authErrorRef.current) {
        console.warn("⚠️ [SESSION] Global IndexedDB error detected, switching to stored session:", err?.message)
        authErrorRef.current = true
        setUseStoredSession(true)
      }
    }
    
    window.addEventListener("error", handleError)
    return () => window.removeEventListener("error", handleError)
  }, [])
  
  // Always call hook unconditionally
  try {
    const result = useAuthHook()
    user = (result as any)?.user ?? null
    isLoading = (result as any)?.isLoading ?? false
  } catch (err: any) {
    // This catch may not work for async errors, but helps with sync ones
    const isIndexedDBError = err?.message?.includes("IDBDatabase") || 
                            err?.message?.includes("object stores") ||
                            err?.name === "NotFoundError"
    
    if (isIndexedDBError && !authErrorRef.current) {
      console.warn("⚠️ [SESSION] IndexedDB error in hook, switching to stored session:", err.message)
      authErrorRef.current = true
      setUseStoredSession(true)
    }
    user = null
    isLoading = false
  }

  useEffect(() => {
    if (typeof window === "undefined") {
      setMockReady(true)
      return
    }

    if (IS_MOCK_MODE) {
      try {
        const stored = localStorage.getItem("shapp-dev-session")
        if (stored) {
          const parsed = JSON.parse(stored) as InstantSession
          if (parsed?.user) {
            setMockSession(parsed)
          }
        }
      } catch (error) {
        console.warn("⚠️ [SESSION] Failed to load mock session:", error)
      }
    }

    setMockReady(true)
  }, [])

  const role = (user as any)?.role
  const tier = (user as any)?.tier
  const avatar = (user as any)?.avatar
  const timezone = (user as any)?.timezone

  const readStoredValue = (key: string) => {
    if (typeof window === "undefined") {
      return null
    }
    return sessionStorage.getItem(key) || localStorage.getItem(key)
  }

  const data: InstantSession = useMemo(() => {
    // If IndexedDB failed, use stored session
    if (useStoredSession || authErrorRef.current) {
      const stored = getStoredSession()
      if (stored) {
        return stored
      }
      return null
    }

    if (IS_MOCK_MODE) {
      return mockSession
    }

    if (!user) {
      return null
    }

    const userName = (user as any)?.name
    const storedRole = (role as string | undefined) ?? readStoredValue("user-role") ?? undefined
    const storedTier = (tier as string | undefined) ?? readStoredValue("user-tier") ?? undefined
    const storedTimezone = (timezone as string | undefined) ?? readStoredValue("user-timezone") ?? undefined

    return {
      user: {
        id: user.id,
        email: user.email ?? undefined,
        name: userName ?? undefined,
        role: storedRole,
        tier: storedTier,
        avatar: avatar ?? undefined,
        timezone: storedTimezone,
      },
    }
  }, [user, role, tier, avatar, timezone, useStoredSession, mockSession])

  // If IndexedDB failed, use stored session and skip loading
  if (useStoredSession || authErrorRef.current) {
    const stored = getStoredSession()
    return {
      data: stored,
      status: stored ? "authenticated" : "unauthenticated",
    }
  }

  if (IS_MOCK_MODE) {
    return {
      data: mockSession,
      status: !mockReady ? "loading" : mockSession ? "authenticated" : "unauthenticated",
    }
  }

  const status = isLoading ? "loading" : user ? "authenticated" : "unauthenticated"

  return { data, status }
}

export async function signOut(options?: { redirect?: boolean }) {
  if (!IS_MOCK_MODE) {
    try {
      await db.auth.signOut()
    } catch (error) {
      console.error("Failed to sign out:", error)
    }
  }

  if (typeof window !== "undefined") {
    sessionStorage.clear()
    localStorage.removeItem("shapp-dev-session")
    localStorage.removeItem("user-email")
    localStorage.removeItem("user-role")
    localStorage.removeItem("user-tier")
    localStorage.removeItem("user-timezone")
  }

  if (options?.redirect !== false && typeof window !== "undefined") {
    window.location.href = "/login"
  }
}
